import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import styled from 'styled-components';
import { Event, EVENT_TYPES, STATUS_CONFIG } from '../types';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const CalendarContainer = styled.div`
  background: #191919;
  border-radius: 0.75rem;
  padding: 0;
  border: 1px solid #383836;
  border-top: none;
  border-left: none;
  border-right: none;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  .rbc-calendar {
    background: #191919;
    color: #e5e7eb;
    padding: 0;
    height: auto !important;
  }

  .rbc-toolbar {
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
  }

  .rbc-toolbar-label {
    margin: 0;
    padding: 0;
    flex: 0 0 auto;
    text-align: left;
  }

  .rbc-toolbar button {
    border-radius: 0.4rem;
    border: 1px solid #383836;
    background: #101117;
    color: #e5e7eb;
    padding: 0.35rem 0.9rem;
    font-weight: 500;
    font-size: 0.85rem;
    transition: all 0.15s;

    &:hover {
      background: #18181f;
    }

    &.rbc-active {
      background: #e5e7eb;
      color: #050509;
      border-color: #e5e7eb;
    }
  }

  /* Month header cells (days of week) – only bottom divider, no left/right on Sun/Sat */
  .rbc-month-view .rbc-month-header .rbc-header {
    background: #191919;
    color: #7E7A76;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.5rem 0.5rem;
    border: none;
    border-bottom: 1px solid #383836;
    border-left: none !important;
    border-right: none !important;
  }

  .rbc-time-view .rbc-header {
    border-bottom: 1px solid #383836;
  }

  .rbc-time-slot {
    color: #4b5563;
  }

  .rbc-month-view,
  .rbc-time-view {
    border: 1px solid #383836;
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0.5rem;
  }

  /* Let month rows grow to fit multiple events in a day */
  .rbc-month-view {
    height: auto !important;
    min-height: 0;
  }
  .rbc-month-row {
    flex: 0 0 auto !important;
    min-height: 7rem;
    height: auto !important;
    overflow: visible !important;
  }
  .rbc-month-row + .rbc-month-row {
    border-top: 1px solid #383836;
  }
  .rbc-month-row:last-child {
    border-bottom: 1px solid #383836;
  }
  .rbc-row-bg {
    flex: 1 1 auto !important;
    min-height: 5rem;
    overflow: visible !important;
  }

  /* Outermost strokes on first and last day columns (numbered days + content only, not header) */
  .rbc-row-bg .rbc-day-bg-wrapper:first-child {
    border-left: 1px solid #383836;
    box-sizing: border-box;
  }
  .rbc-row-bg .rbc-day-bg-wrapper:last-child {
    border-right: 1px solid #383836;
    box-sizing: border-box;
  }
  .rbc-month-view .rbc-row .rbc-date-cell:first-child {
    border-left: 1px solid #383836;
    box-sizing: border-box;
  }
  .rbc-month-view .rbc-row .rbc-date-cell:last-child {
    border-right: 1px solid #383836;
    box-sizing: border-box;
  }

  /* Vertical dividers between day columns */
  .rbc-row-bg .rbc-day-bg-wrapper + .rbc-day-bg-wrapper {
    border-left: 1px solid #383836;
    box-sizing: border-box;
  }
  .rbc-month-view .rbc-row .rbc-date-cell {
    position: relative;
    overflow: hidden;
  }
  .rbc-month-view .rbc-row .rbc-date-cell + .rbc-date-cell::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background: #383836;
    pointer-events: none;
  }

  /* Saturday and Sunday column fill */
  .rbc-row-bg .rbc-day-bg-wrapper:first-child .rbc-day-bg,
  .rbc-row-bg .rbc-day-bg-wrapper:last-child .rbc-day-bg {
    background: #191919 !important;
  }
  .rbc-month-view .rbc-row .rbc-date-cell:first-child,
  .rbc-month-view .rbc-row .rbc-date-cell:last-child {
    background: #191919 !important;
  }
  .rbc-month-view .rbc-month-header .rbc-header:first-child,
  .rbc-month-view .rbc-month-header .rbc-header:last-child {
    background: #191919 !important;
  }

  .rbc-off-range-bg {
    background: #191919;
  }

  .rbc-today {
    background: #18181f !important;
  }

  .rbc-event {
    background: #373737 !important;
    border-radius: 0.5rem;
    border: none;
    padding: 0.35rem 0.5rem;
    margin: 1px 4px;
    font-size: 0.8rem;
    font-weight: 500;
    min-height: 2.75rem;
    color: #e5e7eb;
    box-sizing: border-box;
    max-width: calc(100% - 8px);
  }

  .rbc-event-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.45rem;
    min-height: 2.5em;
  }

  .calendar-event-title {
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .calendar-event-badge {
    display: inline-block;
    padding: 0.1rem 0.35rem;
    border-radius: 0.25rem;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    line-height: 1.2;
  }

  /* Date number in top right of cell */
  .rbc-date-cell {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 4px 6px 0 0;
    text-align: right;
  }

  /* Circle around today's date number (skip the copy shown in a neighboring month's overflow row) */
  .rbc-date-cell.rbc-now:not(.rbc-off-range) .rbc-button-link {
    width: 24px;
    height: 24px;
    margin-top: -3px;
    border-radius: 9999px;
    border: 1.5px solid #e5e7eb;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    color: #e5e7eb;
    font-weight: 600;
  }

  /* Stack day cells above events row so hover + add button work */
  .rbc-row-content {
    z-index: 1;
  }
  /* Reserve top of each day for add-event icon; events start 8px from top */
  .rbc-month-view .rbc-row-content .rbc-row-content-scroll-container {
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .rbc-month-view .rbc-row-content .rbc-row-content-scroll-container .rbc-row {
    flex: 0 0 auto;
  }
  .rbc-row-bg .rbc-day-bg-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 2;
  }
  .rbc-row-bg .rbc-day-bg-wrapper .rbc-day-bg {
    background: transparent !important;
  }

  .calendar-day-add-btn {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 1px solid #383836;
    background: #191919;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    z-index: 3;
  }

  .calendar-day-add-btn:hover {
    background: #383836;
    color: #e5e7eb;
    border-color: #e5e7eb;
  }
`;

/** Single toolbar above the scroll section: month/year + ‹ Today › */
const CalendarHeader = styled.div`
  flex-shrink: 0;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;

  .calendar-header-label {
    margin: 0;
    padding: 0;
    font-weight: 500;
    font-size: 1rem;
    color: #e5e7eb;
  }

  .calendar-header-btns {
    display: flex;
    align-items: center;
    gap: 0;
  }

  .calendar-header-btns button {
    border-radius: 0.4rem;
    border: 1px solid #383836;
    background: #101117;
    color: #e5e7eb;
    padding: 0.35rem 0.9rem;
    font-weight: 500;
    font-size: 0.85rem;
    transition: all 0.15s;
  }

  .calendar-header-btns button:hover:not(:disabled) {
    background: #18181f;
  }

  .calendar-header-btns button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Single sticky row: days of week (Sun–Sat), does not scroll, no repeat per month */
const StickyDaysRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  isolation: isolate;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #191919;
  color: #7e7a76;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.5rem 0.5rem;
  border-bottom: 1px solid #383836;
  margin: 0;
  flex-shrink: 0;
  box-shadow: 0 2px 0 0 #191919;

  & > span {
    text-align: center;
    background: #191919;
  }

  & > span:first-child,
  & > span:last-child {
    background: #191919;
  }
`;

/** Scrollable column that stacks months vertically. Scrollbar only visible when scrolling or hovering, at viewport right edge. */
const CalendarScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-bottom: 1rem;
  scrollbar-width: thin;

  /* Scrollbar: hidden by default, visible when scrolling or hovering */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
    transition: background 0.2s ease;
  }
  &.show-scrollbar::-webkit-scrollbar-thumb,
  &:hover::-webkit-scrollbar-thumb {
    background: #3f3f46;
  }
  &.show-scrollbar::-webkit-scrollbar-thumb:hover,
  &:hover::-webkit-scrollbar-thumb:hover {
    background: #52525b;
  }

  /* Keep scrolling content below the sticky header (vertical dividers must not overlap it) */
  & > section {
    position: relative;
    z-index: 0;
  }

  /* Hide each month's own day-of-week header; we use one sticky row above */
  .rbc-month-header {
    display: none !important;
  }
`;

/** Wrapper for each month block in the stack (no toolbar inside, no gap between months) */
const MonthSection = styled.section`
  flex: 0 0 auto;
  padding-top: 0;
`;

interface CalendarViewProps {
  events: Event[];
  /** When add icon on a day is clicked: (date, iconRect) to open the add-event menu at that position */
  onAddEventInDay?: (date: Date, anchorRect: DOMRect) => void;
}

const AddIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

interface DateCellWrapperProps {
  value: Date;
  range?: Date[];
  children: React.ReactNode;
  onAddEventInDay?: (date: Date, anchorRect: DOMRect) => void;
}

const DateCellWrapperComponent: React.FC<DateCellWrapperProps> = ({ value, children, onAddEventInDay }) => {
  const [hover, setHover] = useState(false);
  const handleAddClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onAddEventInDay?.(value, e.currentTarget.getBoundingClientRect());
  };
  return (
    <div
      className="rbc-day-bg-wrapper"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {hover && onAddEventInDay && (
        <button
          type="button"
          className="calendar-day-add-btn"
          onClick={handleAddClick}
          aria-label="Add event"
        >
          <AddIcon />
        </button>
      )}
    </div>
  );
};

interface CalendarEventWrapperProps {
  event: { title?: string; resource?: Event };
  children?: React.ReactNode;
}

const CalendarEventComponent: React.FC<CalendarEventWrapperProps> = ({ event: calEvent }) => {
  const resource = calEvent.resource as Event | undefined;
  const status = resource?.status;
  const statusConfig = status ? STATUS_CONFIG[status] : null;
  return (
    <div className="rbc-event-content">
      <span className="calendar-event-title">{calEvent.title ?? ''}</span>
      {statusConfig && (
        <span
          className="calendar-event-badge"
          style={{
            backgroundColor: statusConfig.color,
            color: '#f9fafb',
          }}
        >
          {statusConfig.label}
        </span>
      )}
    </div>
  );
};

const MONTHS_BACK = 2;
const MONTHS_FORWARD = 14;

function getMonthRange(): Date[] {
  const start = new Date();
  start.setDate(1);
  start.setMonth(start.getMonth() - MONTHS_BACK);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setDate(1);
  end.setMonth(end.getMonth() + MONTHS_FORWARD);
  end.setHours(0, 0, 0, 0);
  const months: Date[] = [];
  const cur = new Date(start.getTime());
  while (cur.getTime() <= end.getTime()) {
    months.push(new Date(cur.getTime()));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

const CalendarView: React.FC<CalendarViewProps> = ({ events, onAddEventInDay }) => {
  const monthDates = useMemo(() => getMonthRange(), []);
  const monthSectionRefs = useRef<(HTMLElement | null)[]>([]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const stickyDaysRowRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => new Date(), []);
  const todayMonthIndex = useMemo(() => {
    const y = today.getFullYear();
    const m = today.getMonth();
    return monthDates.findIndex(
      (d) => d.getFullYear() === y && d.getMonth() === m
    );
  }, [monthDates, today]);

  const [visibleMonthIndex, setVisibleMonthIndex] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollbarHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToMonth = useCallback((index: number) => {
    if (index < 0 || index >= monthDates.length) return;
    const el = monthSectionRefs.current[index];
    const container = scrollAreaRef.current;
    setVisibleMonthIndex(index);
    if (!el || !container) return;
    // Land the month just below the sticky Sun–Sat row so its first week isn't hidden.
    const stickyHeight = stickyDaysRowRef.current?.offsetHeight ?? 0;
    container.scrollTop +=
      el.getBoundingClientRect().top - container.getBoundingClientRect().top - stickyHeight;
  }, [monthDates.length]);

  const updateVisibleMonth = useCallback(() => {
    const container = scrollAreaRef.current;
    if (!container) return;
    // Measure from the scroll area's top edge; offsetTop is relative to an outer ancestor.
    const containerTop = container.getBoundingClientRect().top;
    const threshold = 120;
    let index = 0;
    for (let i = 0; i < monthSectionRefs.current.length; i++) {
      const el = monthSectionRefs.current[i];
      if (!el) break;
      if (el.getBoundingClientRect().top - containerTop <= threshold) index = i;
    }
    setVisibleMonthIndex(index);
  }, []);

  const handleScroll = useCallback(() => {
    updateVisibleMonth();
    setShowScrollbar(true);
    if (scrollbarHideTimeoutRef.current) clearTimeout(scrollbarHideTimeoutRef.current);
    scrollbarHideTimeoutRef.current = setTimeout(() => {
      setShowScrollbar(false);
      scrollbarHideTimeoutRef.current = null;
    }, 1000);
  }, [updateVisibleMonth]);

  useEffect(() => {
    return () => {
      if (scrollbarHideTimeoutRef.current) clearTimeout(scrollbarHideTimeoutRef.current);
    };
  }, []);

  // Open on the month of the trip's first event; with no events (or one outside the range), open on today.
  useEffect(() => {
    const first = events.reduce<Date | null>(
      (min, e) => (!min || e.date.getTime() < min.getTime() ? e.date : min),
      null
    );
    const firstIndex = first
      ? monthDates.findIndex(
          (d) => d.getFullYear() === first.getFullYear() && d.getMonth() === first.getMonth()
        )
      : -1;
    if (!first || firstIndex < 0) {
      scrollToMonth(todayMonthIndex);
      return;
    }
    scrollToMonth(firstIndex);
    // Then bring the event's week row up under the sticky weekday row so the event itself is in view.
    const container = scrollAreaRef.current;
    const rows = monthSectionRefs.current[firstIndex]?.querySelectorAll('.rbc-month-row');
    const weekIndex = Math.floor((first.getDate() - 1 + monthDates[firstIndex].getDay()) / 7);
    const row = rows?.[weekIndex] as HTMLElement | undefined;
    if (container && row && weekIndex > 0) {
      const stickyHeight = stickyDaysRowRef.current?.offsetHeight ?? 0;
      container.scrollTop +=
        row.getBoundingClientRect().top - container.getBoundingClientRect().top - stickyHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = scrollAreaRef.current;
    if (!container) return;
    updateVisibleMonth();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, updateVisibleMonth]);

  const DateCellWrapper = useCallback(
    (props: { value: Date; range?: Date[]; children: React.ReactNode }) => (
      <DateCellWrapperComponent {...props} onAddEventInDay={onAddEventInDay} />
    ),
    [onAddEventInDay]
  );

  const calendarEvents = useMemo(() => {
    return events.map(event => {
      const end = event.endDate
        ? new Date(event.endDate.getTime())
        : new Date(event.date.getTime() + 60 * 60 * 1000);
      return {
        id: event.id,
        title: `${EVENT_TYPES[event.type].emoji} ${event.name}`,
        start: event.date,
        end,
        resource: event,
        style: {
          border: 'none',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          padding: '0.25rem 0.5rem',
        },
      };
    });
  }, [events]);

  const eventStyleGetter = (event: any) => {
    return {
      style: event.style || {},
    };
  };

  const onSelectEvent = (event: any) => {
    console.log('Selected event:', event.resource);
    // TODO: Open event details modal
  };

  const formats = useMemo(
    () => ({ dateFormat: 'D' }),
    []
  );

  const currentMonthLabel = useMemo(
    () => moment(monthDates[visibleMonthIndex]).format('MMMM YYYY'),
    [monthDates, visibleMonthIndex]
  );

  return (
    <CalendarContainer>
      <CalendarHeader>
        <span className="calendar-header-label">{currentMonthLabel}</span>
        <span className="calendar-header-btns">
          <button
            type="button"
            onClick={() => scrollToMonth(visibleMonthIndex - 1)}
            disabled={visibleMonthIndex <= 0}
            aria-label="Previous month"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => scrollToMonth(todayMonthIndex)}
            aria-label="Go to today"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => scrollToMonth(visibleMonthIndex + 1)}
            disabled={visibleMonthIndex >= monthDates.length - 1}
            aria-label="Next month"
          >
            ›
          </button>
        </span>
      </CalendarHeader>
      <CalendarScrollArea
        ref={scrollAreaRef}
        onScroll={handleScroll}
        onMouseEnter={() => setShowScrollbar(true)}
        onMouseLeave={() => setShowScrollbar(false)}
        className={showScrollbar ? 'show-scrollbar' : ''}
      >
        <StickyDaysRow ref={stickyDaysRowRef}>
          <span>Sun</span>
          <span>Mon</span>
          <span>Tue</span>
          <span>Wed</span>
          <span>Thu</span>
          <span>Fri</span>
          <span>Sat</span>
        </StickyDaysRow>
        {monthDates.map((monthStart, index) => (
          <MonthSection
            key={monthStart.toISOString()}
            ref={(el) => {
              monthSectionRefs.current[index] = el;
            }}
          >
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 'auto' }}
              onSelectEvent={onSelectEvent}
              date={monthStart}
              view="month"
              views={['month']}
              eventPropGetter={eventStyleGetter}
              formats={formats}
              popup
              showAllEvents
              showMultiDayTimes
              step={60}
              timeslots={1}
              components={{
                toolbar: () => null,
                dateCellWrapper: DateCellWrapper,
                event: CalendarEventComponent,
              }}
            />
          </MonthSection>
        ))}
      </CalendarScrollArea>
    </CalendarContainer>
  );
};

export default CalendarView;

